import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { port } from '../../App';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { getFacebookLeadsAPI, syncFacebookLeadAPI } from '../../api-wa/facebookApi';
import { LeadDetailModal } from '../Facebook/FacebookLeadsHelper';

const LocalLoader = () => (
    <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
);

const OtherLeadsBucket = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Sub tabs
    const [subTab, setSubTab] = useState('facebook');

    // Facebook Leads states
    const [fbLeads, setFbLeads] = useState([]);
    const [fbTotalCount, setFbTotalCount] = useState(0);
    const [fbTotalPages, setFbTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    
    // Checked FB Leads
    const [selectedFbLeads, setSelectedFbLeads] = useState([]);

    // Recruiter Assignment states
    const [recruiters, setRecruiters] = useState([]);
    const [assignTargetRecruiter, setAssignTargetRecruiter] = useState('');
    const user = JSON.parse(sessionStorage.getItem('user'));
    const isManagerOrAdmin = user?.Disgnation === 'Admin' || user?.Disgnation === 'HR' || user?.Designation === 'Admin' || user?.Designation === 'HR';
    const canAssignLeads = isManagerOrAdmin || user?.user_permissions?.lead_assign_access;

    // Details Modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [activeDetailLead, setActiveDetailLead] = useState(null);

    // Filters & Search
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const fetchFacebookLeads = async () => {
        setIsLoading(true);
        try {
            const params = {
                page,
                pageSize
            };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (searchQuery) params.search = searchQuery;

            const res = await getFacebookLeadsAPI(params);
            if (res && Array.isArray(res.data)) {
                setFbLeads(res.data);
                setFbTotalCount(res.count || 0);
                setFbTotalPages(res.pages || 1);
            } else {
                toast.error("Failed to load Facebook leads.");
            }
        } catch (err) {
            console.error("fetchFacebookLeads error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRecruiters = () => {
        axios.get(`${port}/root/ScreeningAssigning/`)
            .then(res => {
                setRecruiters(res.data || []);
            })
            .catch(err => {
                console.error("Error fetching recruiters:", err);
            });
    };

    useEffect(() => {
        if (subTab === 'facebook') {
            fetchFacebookLeads();
        }
    }, [page, subTab, startDate, endDate]);

    useEffect(() => {
        fetchRecruiters();
    }, []);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        fetchFacebookLeads();
    };

    const handleSelectAllFb = (e) => {
        if (e.target.checked) {
            setSelectedFbLeads(fbLeads.map(l => l.leadgen_id));
        } else {
            setSelectedFbLeads([]);
        }
    };

    const handleSelectFbLead = (e, id) => {
        if (e.target.checked) {
            setSelectedFbLeads(prev => [...prev, id]);
        } else {
            setSelectedFbLeads(prev => prev.filter(item => item !== id));
        }
    };

    // Bulk transfer / assign leads
    const handleBulkAssign = () => {
        if (!selectedFbLeads.length) {
            toast.warning("Please select at least one lead.");
            return;
        }
        if (!assignTargetRecruiter) {
            toast.warning("Please select a recruiter to assign to.");
            return;
        }

        setIsLoading(true);
        axios.post(`${port.replace(/\/$/, '')}/api/facebook/pass-crm`, {
            leads: selectedFbLeads,
            lead_type: 'b2c',
            assigned_to: assignTargetRecruiter
        })
            .then(res => {
                toast.success("Successfully assigned and transferred leads.");
                setSelectedFbLeads([]);
                setAssignTargetRecruiter('');
                fetchFacebookLeads();
            })
            .catch(err => {
                console.error("Assignment error:", err);
                toast.error(err.response?.data?.error || "Failed to transfer leads.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleIndividualSync = async (leadgenId) => {
        setIsLoading(true);
        try {
            const res = await syncFacebookLeadAPI({ leadgen_id: leadgenId });
            if (res.success) {
                toast.success("Lead re-synced successfully.");
                fetchFacebookLeads();
            } else {
                toast.error(res.message || "Failed to re-sync lead.");
            }
        } catch (err) {
            console.error("Sync error:", err);
            const errMsg = err.response?.data?.message || err.response?.data || "Network error during sync.";
            toast.error(errMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const convertIndianTime = (unixTimestamp) => {
        if (!unixTimestamp) return '—';
        return new Date(unixTimestamp * 1000).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            hour12: true,
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    };

    return (
        <div className="container-fluid p-4 font-popins text-slate-800 poppins">
            {/* Top Main Blue Header Bar */}
            <div className="bg-[#2563EB] text-white rounded-xl px-4 py-3 flex justify-between items-center shadow-sm flex-wrap gap-3 mb-4">
                <h5 className="font-bold mb-0 text-sm md:text-base flex items-center gap-3">
                    <button
                        onClick={() => navigate('/activity')}
                        className="bg-transparent border-0 text-white p-0 hover:text-slate-200 transition-colors cursor-pointer flex items-center justify-center"
                        title="Back to Dashboard"
                    >
                        <i className="fas fa-arrow-left text-base"></i>
                    </button>
                    Other Leads Bucket
                </h5>
            </div>

            {/* Sub-tabs bar: Facebook Leads, Walkin, Bots, etc. */}
            <div className="flex gap-2 my-3 overflow-x-auto pb-2 scrollbar-thin">
                {[
                    { id: 'facebook', label: 'Facebook Leads', icon: 'fab fa-facebook text-blue-600' },
                    { id: 'walkin', label: 'Walkin Leads', icon: 'fas fa-walking text-slate-600' },
                    { id: 'fta_wa', label: 'FTA whatsapp bot', icon: 'fab fa-whatsapp text-emerald-600' },
                    { id: 'fta_web', label: 'FTA web bot', icon: 'fas fa-robot text-indigo-600' },
                    { id: 'mtm_wa', label: 'MTM whatsapp bot', icon: 'fab fa-whatsapp text-emerald-600' },
                    { id: 'mtm_web', label: 'MTM Web bot', icon: 'fas fa-robot text-indigo-600' },
                    { id: 'sla_wa', label: 'SLA whatsapp bot', icon: 'fab fa-whatsapp text-emerald-600' },
                    { id: 'sla_web', label: 'SLA web bot', icon: 'fas fa-robot text-indigo-600' },
                    { id: 'justdial', label: 'JustDial Leads', icon: 'fas fa-list text-amber-600' }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setSubTab(tab.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap shadow-sm border transition-all ${
                            subTab === tab.id
                                ? 'bg-white border-blue-500 text-blue-600 font-bold'
                                : 'bg-[#f8fafc] border-slate-200 text-slate-600 hover:bg-white'
                        }`}
                    >
                        <i className={tab.icon}></i> {tab.label}
                    </button>
                ))}
            </div>

            {/* Main Content Area */}
            {subTab === 'facebook' ? (
                <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                    {/* Header line + Date Filters + Search input */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <h4 className="text-base font-bold text-slate-700 m-0">Facebook Leads</h4>
                            
                            {/* Selected Count Badge */}
                            {selectedFbLeads.length > 0 && (
                                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        onChange={() => setSelectedFbLeads([])}
                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-xs font-semibold text-slate-600">
                                        Selected ({selectedFbLeads.length}) leads
                                    </span>
                                </div>
                            )}

                            {/* Inline Assignment Dropdown */}
                            {canAssignLeads && selectedFbLeads.length > 0 && (
                                <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                                    <span className="text-xs text-slate-500 font-medium">Assign to:</span>
                                    <select
                                        value={assignTargetRecruiter}
                                        onChange={(e) => setAssignTargetRecruiter(e.target.value)}
                                        className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 py-0"
                                    >
                                        <option value="">Select Employee</option>
                                        {recruiters.map((rec) => (
                                            <option key={rec.id} value={rec.EmployeeId}>
                                                {rec.Name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={handleBulkAssign}
                                        disabled={!assignTargetRecruiter}
                                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-semibold rounded text-[10px] transition-all border-0 cursor-pointer"
                                    >
                                        Assign
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Filters & Search Form */}
                        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3">
                            {/* Date inputs */}
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0"
                                />
                                <span className="text-xs text-slate-400">to</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0"
                                />
                            </div>

                            {/* Search bar */}
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all w-[260px]">
                                <i className="fas fa-search text-slate-400 text-xs"></i>
                                <input
                                    type="text"
                                    placeholder="Search by name, phone..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                                />
                            </div>
                            <button
                                type="submit"
                                className="px-4 py-1.5 bg-[#2563EB] hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition-colors border-0"
                            >
                                Search
                            </button>
                        </form>
                    </div>

                    {/* Table View */}
                    <div className="table-responsive overflow-x-auto min-h-[400px]">
                        {isLoading ? (
                            <LocalLoader />
                        ) : (
                            <table className="w-full table-fixed min-w-[1200px] border-collapse border border-slate-200">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-800 font-semibold text-sm whitespace-nowrap">
                                        <th style={{ width: '4%' }} className="p-2 text-center border border-slate-200">
                                            <input
                                                type="checkbox"
                                                checked={fbLeads.length > 0 && fbLeads.every(lead => selectedFbLeads.includes(lead.leadgen_id))}
                                                onChange={handleSelectAllFb}
                                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                        </th>
                                        <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Full Name</th>
                                        <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Phone</th>
                                        <th style={{ width: '18%' }} className="p-2 text-center border border-slate-200">Enquired At</th>
                                        <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Leadgen ID</th>
                                        <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Ad ID/Name</th>
                                        <th style={{ width: '10%' }} className="p-2 text-center border border-slate-200">Status</th>
                                        <th style={{ width: '10%' }} className="p-2 text-center border border-slate-200">CRM Sync</th>
                                        <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Created At</th>
                                        <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fbLeads.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-8 italic text-slate-400 border border-slate-200 text-xs">
                                                No Facebook leads found.
                                            </td>
                                        </tr>
                                    ) : (
                                        fbLeads.map((lead, index) => {
                                            const isChecked = selectedFbLeads.includes(lead.leadgen_id);
                                            const leadData = lead.facebook_ad_detail || {};
                                            const enquiredTime = lead.lead_gen_data?.created_time;
                                            
                                            return (
                                                <tr key={lead.id} className="hover:bg-slate-50 transition-colors text-xs text-slate-700 border-b">
                                                    <td className="p-2 text-center border border-slate-200">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={(e) => handleSelectFbLead(e, lead.leadgen_id)}
                                                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 font-semibold text-indigo-600 capitalize">
                                                        {leadData.full_name || '—'}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 font-semibold">
                                                        {leadData.phone || '—'}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 text-slate-500">
                                                        {enquiredTime ? convertIndianTime(enquiredTime) : '—'}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 font-mono text-[10px]">
                                                        {lead.leadgen_id}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 text-slate-600">
                                                        {lead.ad_name ? `${lead.ad_name} (${lead.ad_id})` : lead.ad_id || '—'}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${lead.synced ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                            {lead.synced ? 'SYNCED' : 'PENDING'}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${lead.crm_sync ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                            {lead.crm_sync ? 'SYNCED' : 'PENDING'}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200 text-slate-500">
                                                        {lead.created_at ? new Date(lead.created_at).toLocaleString() : '—'}
                                                    </td>
                                                    <td className="p-2 text-center border border-slate-200">
                                                        <div className="flex gap-1 justify-center">
                                                            <button
                                                                onClick={() => {
                                                                    setActiveDetailLead(lead);
                                                                    setShowDetailModal(true);
                                                                }}
                                                                className="px-2 py-0.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-600 text-[10px] font-semibold transition-all border-0 cursor-pointer"
                                                            >
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={() => handleIndividualSync(lead.leadgen_id)}
                                                                className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-semibold transition-all border-0 cursor-pointer"
                                                            >
                                                                Re-sync
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center italic text-slate-400 shadow-sm">
                    No records found in this category.
                </div>
            )}

            {/* Modals */}
            <LeadDetailModal
                show={showDetailModal}
                onHide={() => { setShowDetailModal(false); setActiveDetailLead(null); }}
                data={activeDetailLead}
            />
        </div>
    );
};

export default OtherLeadsBucket;
